/**
 * Scan Courrier Papier
 * - Capture photo (caméra ou upload)
 * - Envoi backend → OCR Vision IA → classement → Drive
 * - Affichage extraction + lien fiche client si match
 * - Liste des courriers scannés
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Camera, Upload, Loader2, FileImage, AlertTriangle, Clock, User, Trash2,
  Folder, ChevronRight, MailOpen, Euro, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const URGENCE_COLOR = {
  urgente:  'bg-red-500 text-white',
  haute:    'bg-orange-500 text-white',
  normale:  'bg-blue-500 text-white',
  basse:    'bg-gray-400 text-white',
};

const TYPE_LABEL = {
  facture_fournisseur:    '💰 Facture fournisseur',
  courrier_administratif: '🏛️ Administratif',
  lettre_client:          '👤 Lettre client',
  publicite:              '📰 Publicité',
  convocation:            '⚖️ Convocation',
  mise_en_demeure:        '🚨 Mise en demeure',
  contrat_recu:           '📋 Contrat reçu',
  devis_recu:             '📄 Devis reçu',
  releve_bancaire:        '🏦 Relevé bancaire',
  autre:                  '📃 Autre',
};

export default function ScanCourrierPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('scan');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [courriers, setCourriers] = useState([]);
  const [filterUrgence, setFilterUrgence] = useState('all');
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const fetchCourriers = async () => {
    try {
      const r = await api.get('/scan-courrier' + (filterUrgence !== 'all' ? `?urgence=${filterUrgence}` : ''));
      setCourriers(r.data.courriers || []);
    } catch { /* */ }
  };
  useEffect(() => { fetchCourriers(); }, [filterUrgence]);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
  };

  const handleScan = async () => {
    if (!file) { toast.error('Sélectionne une photo'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (notes) fd.append('notes', notes);
      const r = await api.post('/scan-courrier/analyser', fd, { headers: { 'Content-Type': 'multipart/form-data' }});
      setResult(r.data);
      toast.success('Courrier analysé et classé !');
      fetchCourriers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur scan');
    } finally { setLoading(false); }
  };

  const reset = () => {
    setFile(null); setPreviewUrl(null); setResult(null); setNotes('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce courrier scanné ?')) return;
    try {
      await api.delete(`/scan-courrier/${id}`);
      toast.success('Supprimé');
      fetchCourriers();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-4 max-w-5xl" data-testid="scan-courrier-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Camera className="w-7 h-7 text-primary" />
          Scan Courrier Papier
        </h1>
        <p className="text-muted-foreground">Photographiez votre courrier — Valentine le classe et le range automatiquement dans le Drive</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="scan" data-testid="tab-scan">📷 Scanner</TabsTrigger>
          <TabsTrigger value="liste" data-testid="tab-liste">📂 Mes courriers ({courriers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="scan" className="space-y-3 mt-4">
          {!result && (
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col items-center gap-3">
                  {previewUrl ? (
                    <img src={previewUrl} alt="preview" className="max-w-full max-h-96 rounded-lg shadow" />
                  ) : (
                    <div className="w-full max-w-md aspect-video border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/30">
                      <FileImage className="w-16 h-16 text-muted-foreground" />
                    </div>
                  )}

                  <div className="flex gap-2 flex-wrap justify-center">
                    <input
                      type="file"
                      ref={cameraRef}
                      accept="image/*"
                      capture="environment"
                      style={{display: 'none'}}
                      onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                    <input
                      type="file"
                      ref={fileRef}
                      accept="image/*"
                      style={{display: 'none'}}
                      onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                    <Button onClick={() => cameraRef.current?.click()} data-testid="btn-photo">
                      <Camera className="w-4 h-4 mr-2" /> Photo
                    </Button>
                    <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="btn-upload">
                      <Upload className="w-4 h-4 mr-2" /> Importer
                    </Button>
                    {file && <Button variant="ghost" onClick={reset}>Annuler</Button>}
                  </div>

                  {file && (
                    <Textarea
                      placeholder="Notes optionnelles (urgent, à répondre, classer dans...)"
                      rows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="max-w-md"
                    />
                  )}

                  {file && (
                    <Button size="lg" onClick={handleScan} disabled={loading} data-testid="btn-analyser">
                      {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Camera className="w-5 h-5 mr-2" />}
                      Analyser et classer le courrier
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {result && (
            <Card data-testid="result-courrier">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{TYPE_LABEL[result.extraction?.type_courrier] || '📃 Courrier'}</span>
                  <Badge className={URGENCE_COLOR[result.extraction?.urgence] || URGENCE_COLOR.normale}>
                    {result.extraction?.urgence || 'normale'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid md:grid-cols-2 gap-4">
                  {previewUrl && <img src={previewUrl} alt="" className="rounded border max-h-64 object-contain" />}
                  <div className="space-y-2 text-sm">
                    <p><strong>Expéditeur :</strong> {result.extraction?.expediteur || '?'}</p>
                    <p><strong>Sujet :</strong> {result.extraction?.sujet || '?'}</p>
                    {result.extraction?.date_courrier && <p><strong>Date :</strong> {result.extraction.date_courrier}</p>}
                    {result.extraction?.montant_eur && <p className="flex items-center gap-1"><Euro className="w-4 h-4" /><strong>{result.extraction.montant_eur} €</strong></p>}
                    {result.extraction?.echeance && <p className="flex items-center gap-1"><Calendar className="w-4 h-4 text-red-500" /><strong>Échéance :</strong> {result.extraction.echeance}</p>}
                    {result.client_id_match && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/clients/${result.client_id_match}`)} data-testid="btn-voir-client-match">
                        <User className="w-3 h-3 mr-1" /> Client identifié → fiche
                      </Button>
                    )}
                  </div>
                </div>

                {result.extraction?.resume && (
                  <div className="bg-muted/50 p-3 rounded">
                    <p className="text-xs text-muted-foreground mb-1">Résumé IA :</p>
                    <p className="text-sm">{result.extraction.resume}</p>
                  </div>
                )}

                {(result.extraction?.actions_recommandees || []).length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950/20 p-3 rounded border border-amber-200">
                    <p className="text-xs font-semibold mb-1">⚡ Actions recommandées :</p>
                    <ul className="text-sm list-disc list-inside">
                      {result.extraction.actions_recommandees.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                )}

                {result.action_id && (
                  <div className="bg-red-50 dark:bg-red-950/20 p-3 rounded border border-red-200 text-sm">
                    🚨 Action TODO créée automatiquement (visible sur la fiche client + vue admin Actions en attente)
                  </div>
                )}

                {result.drive_path && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Folder className="w-3 h-3" /> Classé : <code>{result.drive_path}{result.extraction?.sujet ? '...' : ''}</code>
                  </div>
                )}

                <Button onClick={reset} variant="outline" className="w-full">📷 Scanner un autre courrier</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="liste" className="space-y-2 mt-4">
          <div className="flex gap-2 flex-wrap">
            {['all', 'urgente', 'haute', 'normale', 'basse'].map(u => (
              <Button key={u} size="sm" variant={filterUrgence === u ? 'default' : 'outline'} onClick={() => setFilterUrgence(u)}>
                {u === 'all' ? 'Tous' : u.charAt(0).toUpperCase() + u.slice(1)}
              </Button>
            ))}
          </div>

          {courriers.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">Aucun courrier scanné pour le moment.</CardContent></Card>
          ) : courriers.map(c => (
            <Card key={c.id} className={c.urgence === 'urgente' ? 'border-red-400' : c.urgence === 'haute' ? 'border-orange-400' : ''}>
              <CardContent className="p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={URGENCE_COLOR[c.urgence] || URGENCE_COLOR.normale}>{c.urgence}</Badge>
                    <Badge variant="outline">{TYPE_LABEL[c.type_courrier] || c.type_courrier}</Badge>
                    {c.client_id && (
                      <Badge variant="secondary" className="cursor-pointer" onClick={() => navigate(`/clients/${c.client_id}`)}>
                        <User className="w-3 h-3 mr-1" /> Client lié
                      </Badge>
                    )}
                  </div>
                  <p className="font-semibold mt-1">{c.expediteur} — {c.sujet}</p>
                  {c.resume && <p className="text-sm text-muted-foreground line-clamp-2">{c.resume}</p>}
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                    {c.date_courrier && <span><Calendar className="w-3 h-3 inline mr-1" />{c.date_courrier}</span>}
                    {c.montant_eur && <span><Euro className="w-3 h-3 inline mr-1" />{c.montant_eur} €</span>}
                    {c.echeance && <span className="text-red-500"><Clock className="w-3 h-3 inline mr-1" />Échéance {c.echeance}</span>}
                    {c.drive_path && <span><Folder className="w-3 h-3 inline mr-1" />{c.drive_path}</span>}
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => handleDelete(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
